import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, UserPlus, Shield, Mail, Trash2, ChevronDown, Crown, BadgeCheck,
  Eye, Wrench, Clock, X, Send, Info,
} from "lucide-react";

const ROLE_META = {
  owner:   { label: "Propriétaire",      icon: Crown,       color: "#FFC400", desc: "Tous droits, dont l'abonnement et la suppression du compte." },
  admin:   { label: "Administrateur",    icon: Shield,      color: "#FFC400", desc: "Tous droits sauf gestion de l'abonnement." },
  manager: { label: "Chargé d'affaires", icon: Wrench,      color: "#E10600", desc: "Crée/modifie devis, factures, clients, chantiers. Pas de config." },
  viewer:  { label: "Lecture seule",     icon: Eye,         color: "var(--ink-soft)", desc: "Consulte tout, ne modifie rien." },
};

export default function Team() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/team/members");
      setData(data);
    } catch (e) {
      toast.error("Impossible de charger l'équipe");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="text-tertiary-text flex items-center gap-2"><span className="spinner" /> Chargement…</div>;
  }
  if (!data) return null;

  const isAdmin = data.my_role === "owner" || data.my_role === "admin";
  const isOwner = data.my_role === "owner";

  return (
    <div data-testid="team-page" className="max-w-5xl mx-auto">
      <header className="mb-8 animate-in">
        <div className="flex items-center gap-2 text-xs font-semibold mb-3" style={{ color: "#FFC400" }}>
          <Users size={13} strokeWidth={2} /> Équipe & droits d'accès
        </div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-[2.5rem] sm:text-5xl tracking-tight leading-[1.05]" style={{ fontWeight: 500 }}>
              Votre <span style={{ color: "#FFC400" }}>équipe</span>
            </h1>
            <p className="text-secondary-text mt-3 max-w-3xl text-[15px]">
              Invitez vos collaborateurs et définissez leurs droits. Chaque membre se connecte avec son propre compte Google.
            </p>
          </div>
          {isAdmin && (
            <button onClick={() => setShowInvite(true)} className="btn btn-primary" data-testid="invite-btn">
              <UserPlus size={14} /> Inviter un membre
            </button>
          )}
        </div>
      </header>

      {!isAdmin && (
        <div className="card p-4 mb-6 flex items-start gap-3" style={{ background: "rgba(255,196,0,0.08)", borderColor: "rgba(255,196,0,0.30)" }}>
          <Info size={16} style={{ color: "#FFC400" }} className="shrink-0 mt-0.5" />
          <div className="text-sm">
            Seuls les administrateurs peuvent inviter ou retirer des membres. Vous pouvez consulter la liste ci-dessous.
          </div>
        </div>
      )}

      {/* Members list */}
      <div className="card overflow-hidden mb-6" data-testid="members-list">
        <div className="px-5 py-3 border-b border-[var(--border)] text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--grey)" }}>
          Membres actifs ({data.members.length})
        </div>
        <div className="divide-y divide-[var(--border)]">
          {data.members.map((m) => (
            <MemberRow
              key={m.user_id}
              member={m}
              canManage={isAdmin}
              isOwner={isOwner}
              onChanged={load}
            />
          ))}
        </div>
      </div>

      {/* Pending invitations */}
      {data.pending_invitations.length > 0 && (
        <div className="card overflow-hidden mb-6" data-testid="invitations-list">
          <div className="px-5 py-3 border-b border-[var(--border)] text-xs font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--grey)" }}>
            <Clock size={12} /> Invitations en attente ({data.pending_invitations.length})
          </div>
          <div className="divide-y divide-[var(--border)]">
            {data.pending_invitations.map((inv) => (
              <InvitationRow
                key={inv.id}
                inv={inv}
                canManage={isAdmin}
                onCancelled={load}
              />
            ))}
          </div>
        </div>
      )}

      {/* Role legend */}
      <div className="card p-5">
        <div className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "var(--grey)" }}>Légende des rôles</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.entries(ROLE_META).map(([key, meta]) => (
            <div key={key} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: "var(--carbon)" }}>
              <meta.icon size={16} style={{ color: meta.color }} className="shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <div className="font-semibold text-sm" style={{ color: meta.color }}>{meta.label}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>{meta.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showInvite && (
        <InviteDialog onClose={() => setShowInvite(false)} onCreated={load} isOwner={isOwner} />
      )}
    </div>
  );
}

function MemberRow({ member, canManage, isOwner, onChanged }) {
  const meta = ROLE_META[member.role] || ROLE_META.viewer;
  const Icon = meta.icon;
  const [editing, setEditing] = useState(false);
  const [newRole, setNewRole] = useState(member.role);
  const [busy, setBusy] = useState(false);

  const saveRole = async () => {
    setBusy(true);
    try {
      await api.patch(`/team/members/${member.user_id}/role`, { role: newRole });
      toast.success("Rôle mis à jour");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Retirer ${member.email} de l'entreprise ? Cette personne perdra immédiatement l'accès.`)) return;
    setBusy(true);
    try {
      await api.delete(`/team/members/${member.user_id}`);
      toast.success("Membre retiré");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const editable = canManage && member.role !== "owner";
  return (
    <div className="px-5 py-4 flex items-center gap-4 flex-wrap" data-testid={`member-row-${member.user_id}`}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold" style={{ background: "var(--carbon)", color: meta.color }}>
        {member.picture ? <img src={member.picture} alt="" className="w-full h-full rounded-full object-cover" /> : (member.name?.[0] || "?").toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{member.name || member.email}</div>
        <div className="text-xs truncate" style={{ color: "var(--ink-soft)" }}>{member.email}</div>
      </div>

      {editing ? (
        <div className="flex items-center gap-2">
          <select value={newRole} onChange={(e) => setNewRole(e.target.value)} className="input" style={{ minWidth: 160 }} data-testid={`role-select-${member.user_id}`}>
            <option value="viewer">Lecture seule</option>
            <option value="manager">Chargé d'affaires</option>
            {isOwner && <option value="admin">Administrateur</option>}
          </select>
          <button onClick={saveRole} disabled={busy} className="btn btn-primary btn-sm">Sauver</button>
          <button onClick={() => { setEditing(false); setNewRole(member.role); }} className="btn btn-outline btn-sm">×</button>
        </div>
      ) : (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: `${meta.color}1A`, color: meta.color }}>
          <Icon size={12} /> {meta.label}
        </div>
      )}

      {editable && !editing && (
        <div className="flex items-center gap-1">
          <button onClick={() => setEditing(true)} className="w-8 h-8 rounded-md border border-[var(--border)] hover:border-[#FFC400] hover:text-[#FFC400] inline-flex items-center justify-center transition-colors" title="Modifier le rôle">
            <ChevronDown size={13} />
          </button>
          <button onClick={remove} disabled={busy} className="w-8 h-8 rounded-md border border-[var(--border)] hover:border-[#E10600] hover:text-[#E10600] inline-flex items-center justify-center transition-colors" title="Retirer ce membre" data-testid={`remove-${member.user_id}`}>
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function InvitationRow({ inv, canManage, onCancelled }) {
  const meta = ROLE_META[inv.role] || ROLE_META.viewer;
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm(`Annuler l'invitation pour ${inv.email} ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/team/invitations/${inv.id}`);
      toast.success("Invitation annulée");
      onCancelled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="px-5 py-3 flex items-center gap-4 flex-wrap">
      <Mail size={16} className="text-tertiary-text" />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-sm truncate">{inv.email}</div>
        <div className="text-[11px]" style={{ color: "var(--grey)" }}>
          Invité par {inv.invited_by_email} · {new Date(inv.created_at).toLocaleDateString("fr-FR")}
        </div>
      </div>
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: `${meta.color}1A`, color: meta.color }}>
        <meta.icon size={12} /> {meta.label}
      </div>
      {canManage && (
        <button onClick={cancel} disabled={busy} className="btn btn-outline btn-sm" data-testid={`cancel-inv-${inv.id}`}>
          <X size={13} /> Annuler
        </button>
      )}
    </div>
  );
}

function InviteDialog({ onClose, onCreated, isOwner }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("manager");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/team/invite", { email: email.trim().toLowerCase(), role });
      if (r.data.already_pending) {
        toast.info("Une invitation est déjà en attente pour cet email.");
      } else {
        toast.success("Invitation créée — invitez votre collègue à se connecter avec Google.");
      }
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'invitation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <form onSubmit={submit} className="card p-6 sm:p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="invite-dialog">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl" style={{ fontWeight: 700 }}>Inviter un membre</h2>
          <button type="button" onClick={onClose} className="btn btn-outline btn-sm"><X size={14} /></button>
        </div>

        <label className="block text-sm font-semibold mb-1.5">Email du collaborateur</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="prenom.nom@exemple.fr"
          className="input w-full mb-4"
          data-testid="invite-email"
          autoFocus
        />

        <label className="block text-sm font-semibold mb-1.5">Rôle</label>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="input w-full mb-2" data-testid="invite-role">
          <option value="viewer">Lecture seule — consulte uniquement</option>
          <option value="manager">Chargé d'affaires — devis, factures, clients</option>
          {isOwner && <option value="admin">Administrateur — tous droits sauf abonnement</option>}
        </select>
        <div className="text-xs mb-5" style={{ color: "var(--ink-soft)" }}>
          {ROLE_META[role]?.desc}
        </div>

        <div className="rounded-lg p-3 mb-5 text-xs flex items-start gap-2" style={{ background: "var(--carbon)", color: "var(--ink-soft)" }}>
          <Info size={14} className="shrink-0 mt-0.5" style={{ color: "#FFC400" }} />
          <span>
            Votre collègue devra se connecter à <b>pitboard-app.fr</b> avec ce compte Google. L'invitation sera consommée automatiquement à sa première connexion.
          </span>
        </div>

        <div className="flex justify-start gap-2">
          <button type="submit" disabled={busy} className="btn btn-primary" data-testid="invite-submit">
            <Send size={14} /> {busy ? "Envoi…" : "Envoyer l'invitation"}
          </button>
          <button type="button" onClick={onClose} className="btn btn-outline">Annuler</button>
        </div>
      </form>
    </div>
  );
}
